import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { AxiosResponse } from "axios";
import { RootState } from "../store";
import { axiosApi, getResponseError, getPostfixWithRobotId, handleErrorRTKAndAxiosAsync } from "@/helpers";
import { ENDPOINTS_SES, URL_SES } from "../../constants/api_endpoints";
import {
	IAddScriptProps,
	IAddScriptResponse,
	IDeleteScriptProps,
	IEditScriptProps,
	IGetScriptProps,
	IScriptItem,
	IScriptResponse,
	ISesScriptState,
	ITestScriptProps,
	ITestScriptResponse,
	ScriptType,
} from "../../types/ses/sesScriptTypes";
import { IResponse, RequestStatus, ResponseStatus } from "../../types/statusTypes";

const initialState: ISesScriptState = {
	scriptList: {
		data: [],
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
		openTabWithData: {
			isOpen: false,
			scriptType: "endpoint",
			scriptId: "",
		},
		dictionary: {},
	},
	script: {
		name: "",
		script: "",
		type: "endpoint",
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
	addingScript: {
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
		id: null,
	},
	editingScript: {
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
	deletingScript: {
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
	testingScript: {
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
		data: null,
	},
};

// список скриптов
export const getScriptList = createAsyncThunk("sesScript/list", async (robotId: string, { getState, rejectWithValue }) => {
	try {
		const response: AxiosResponse<IScriptItem[] | IResponse> = await axiosApi.get(
			`${URL_SES}/${ENDPOINTS_SES.SCRIPT_LIST}/${getPostfixWithRobotId((getState() as RootState).sesRobot.robotList.activeRobotVersion, robotId)}`
		);
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

// скрипт
export const getScript = createAsyncThunk("sesScript/data", async ({ robotId, scriptId }: IGetScriptProps, { getState, rejectWithValue }) => {
	try {
		const response: AxiosResponse<IScriptResponse | IResponse> = await axiosApi.get(
			`${URL_SES}/${ENDPOINTS_SES.SCRIPT_GET}/${getPostfixWithRobotId((getState() as RootState).sesRobot.robotList.activeRobotVersion, robotId)}/${scriptId}`
		);
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

// добавление скрипта
export const addScript = createAsyncThunk("sesScript/add", async ({ robotId, name, code, type, scriptId }: IAddScriptProps, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<IAddScriptResponse> = await axiosApi.post(`${URL_SES}/${ENDPOINTS_SES.SCRIPT_ADD}/${robotId}`, {
			data: JSON.stringify({
				name,
				code,
				type,
				id: scriptId,
			}),
		});
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

// изменение скрипта
export const editScript = createAsyncThunk("sesScript/edit", async ({ robotId, scriptId, name, code, type }: IEditScriptProps, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<IResponse> = await axiosApi.post(`${URL_SES}/${ENDPOINTS_SES.SCRIPT_EDIT}/${robotId}/${scriptId}`, {
			data: JSON.stringify({
				name,
				code,
				type,
			}),
		});
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

// удаление скрипта
export const deleteScript = createAsyncThunk("sesScript/delete", async ({ robotId, scriptId }: IDeleteScriptProps, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<IResponse> = await axiosApi.delete(`${URL_SES}/${ENDPOINTS_SES.SCRIPT_DELETE}/${robotId}/${scriptId}`);
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

// тестирование скрипта
export const testScript = createAsyncThunk("sesScript/test", async ({ data, robotId, scriptId, value }: ITestScriptProps, { getState, rejectWithValue }) => {
	try {
		const response: AxiosResponse<ITestScriptResponse> = await axiosApi.post(
			`${URL_SES}/${ENDPOINTS_SES.SCRIPT_TEST}/${getPostfixWithRobotId((getState() as RootState).sesRobot.robotList.activeRobotVersion, robotId)}/${scriptId}`,
			{
				data,
				value,
			}
		);
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

const sesScriptSlice = createSlice({
	name: "sesScript",
	initialState,
	reducers: {
		// изменение данных скрипта в списке
		changeScriptDataInList: (state, action: PayloadAction<{ scriptId: string; name: string }>) => {
			state.scriptList.data = state.scriptList.data.map((scriptItem) => {
				if (action.payload.scriptId === scriptItem.id) return { ...scriptItem, name: action.payload.name };
				else return scriptItem;
			});
		},
		// установка флага переключения вкладки
		setTabToggle: (state, action: PayloadAction<{ isOpen: boolean; scriptType: ScriptType; scriptId: string } | false>) => {
			if (typeof action.payload === "boolean") state.scriptList.openTabWithData = { ...state.scriptList.openTabWithData, isOpen: action.payload };
			else state.scriptList.openTabWithData = action.payload;
		},
		// очистка state
		clearState: (state) => {
			state.scriptList = initialState.scriptList;
			state.script = initialState.script;
			state.addingScript = initialState.addingScript;
			state.editingScript = initialState.editingScript;
			state.deletingScript = initialState.deletingScript;
			state.testingScript = initialState.testingScript;
		},
		// очистка списка скриптов
		clearScriptList: (state) => {
			state.scriptList = initialState.scriptList;
		},
		// очистка скрипта
		clearScript: (state) => {
			state.script = initialState.script;
		},
		// очистка статуса добавления скрипта
		clearAddingScript: (state) => {
			state.addingScript = initialState.addingScript;
		},
		// очистка статуса изменения скрипта
		clearEditingScript: (state) => {
			state.editingScript = initialState.editingScript;
		},
		// очистка статуса удаления скрипта
		clearDeletingScript: (state) => {
			state.deletingScript = initialState.deletingScript;
		},
		// очистка данных тестированияскрипта
		clearTestingScript: (state) => {
			state.testingScript = initialState.testingScript;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(getScriptList.pending, (state) => {
				state.scriptList.status = RequestStatus.LOADING;
			})
			.addCase(getScriptList.fulfilled, (state, action) => {
				if (Array.isArray(action.payload)) {
					state.scriptList.status = RequestStatus.IDLE;
					state.scriptList.data = action.payload.sort((a, b) => {
						if (a.name > b.name) return 1;
						else if (a.name < b.name) return -1;
						else return 0;
					});
					const dictionary: Record<string, string> = {}; // создаем словарь id-name
					action.payload.forEach((scriptElem) => (dictionary[scriptElem.id] = scriptElem.name));
					state.scriptList.dictionary = dictionary;
				} else state.scriptList.status = RequestStatus.FAILED;
			})
			.addCase(getScriptList.rejected, (state, action: PayloadAction<unknown>) => {
				state.scriptList.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.scriptList.error = error;
				state.scriptList.message = message;
			})
			.addCase(getScript.pending, (state) => {
				state.script.status = RequestStatus.LOADING;
			})
			.addCase(getScript.fulfilled, (state, action) => {
				if (action.payload && typeof action.payload === "object" && "data" in action.payload) {
					state.script.status = RequestStatus.IDLE;
					state.script.name = action.payload.data.name;
					state.script.script = action.payload.data.code;
					state.script.type = action.payload.data.type;
				} else state.script.status = RequestStatus.FAILED;
			})
			.addCase(getScript.rejected, (state, action: PayloadAction<unknown>) => {
				state.script.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.script.error = error;
				state.script.message = message;
			})
			.addCase(addScript.pending, (state) => {
				state.addingScript.status = RequestStatus.LOADING;
			})
			.addCase(addScript.fulfilled, (state, action) => {
				if (action.payload) {
					state.addingScript.status = RequestStatus.IDLE;
					state.addingScript.error = action.payload.error;
					state.addingScript.message = action.payload.message;
					if (action.payload.id) {
						state.addingScript.id = action.payload.id;
					}
				} else state.addingScript.status = RequestStatus.FAILED;
			})
			.addCase(addScript.rejected, (state, action: PayloadAction<unknown>) => {
				state.addingScript.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.addingScript.error = error;
				state.addingScript.message = message;
			})
			.addCase(editScript.pending, (state) => {
				state.editingScript.status = RequestStatus.LOADING;
			})
			.addCase(editScript.fulfilled, (state, action) => {
				if (action.payload) {
					state.editingScript.status = RequestStatus.IDLE;
					state.editingScript.error = action.payload.error;
					state.editingScript.message = action.payload.message;
				} else state.editingScript.status = RequestStatus.FAILED;
			})
			.addCase(editScript.rejected, (state, action: PayloadAction<unknown>) => {
				state.editingScript.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.editingScript.error = error;
				state.editingScript.message = message;
			})
			.addCase(deleteScript.pending, (state) => {
				state.deletingScript.status = RequestStatus.LOADING;
			})
			.addCase(deleteScript.fulfilled, (state, action) => {
				if (action.payload) {
					state.deletingScript.status = RequestStatus.IDLE;
					state.deletingScript.error = action.payload.error;
					state.deletingScript.message = action.payload.message;
				} else state.deletingScript.status = RequestStatus.FAILED;
			})
			.addCase(deleteScript.rejected, (state, action: PayloadAction<unknown>) => {
				state.deletingScript.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.deletingScript.error = error;
				state.deletingScript.message = message;
			})
			.addCase(testScript.pending, (state) => {
				state.testingScript.status = RequestStatus.LOADING;
			})
			.addCase(testScript.fulfilled, (state, action) => {
				if (action.payload) {
					state.testingScript.status = RequestStatus.IDLE;
					state.testingScript.error = action.payload.error;
					state.testingScript.message = action.payload.message;
					if ("result" in action.payload) {
						const { error, ...otherData } = action.payload;
						state.testingScript.data = otherData;
					}
				} else state.testingScript.status = RequestStatus.FAILED;
			})
			.addCase(testScript.rejected, (state, action: PayloadAction<unknown>) => {
				state.testingScript.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.testingScript.error = error;
				state.testingScript.message = message;
			});
	},
});

export const {
	changeScriptDataInList,
	setTabToggle,
	clearState,
	clearScriptList,
	clearScript,
	clearAddingScript,
	clearEditingScript,
	clearDeletingScript,
	clearTestingScript,
} = sesScriptSlice.actions;

export const selectScriptList = (state: RootState) => state.sesScript.scriptList;
export const selectScript = (state: RootState) => state.sesScript.script;
export const selectAddingScript = (state: RootState) => state.sesScript.addingScript;
export const selectEditingScript = (state: RootState) => state.sesScript.editingScript;
export const selectDeletingScript = (state: RootState) => state.sesScript.deletingScript;
export const selectTestingScript = (state: RootState) => state.sesScript.testingScript;

export default sesScriptSlice.reducer;
